import React from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import RAGBadge from "../components/RAGBadge";
import { useTheme } from "../DataContext";
import { DEMO_BENEFITS, DEMO_PROJECTS } from "../demoData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, AreaChart, Area,
} from "recharts";

const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;

export default function BenefitsRealization() {
  const { theme } = useTheme();

  const totalPlanned    = DEMO_BENEFITS.reduce((s, b) => s + b.planned, 0);
  const totalProjected  = DEMO_BENEFITS.reduce((s, b) => s + b.projected, 0);
  const totalRealized   = DEMO_BENEFITS.reduce((s, b) => s + b.realized, 0);
  const totalGap        = totalPlanned - totalProjected;
  const avgConfidence   = Math.round(DEMO_BENEFITS.reduce((s, b) => s + b.confidence, 0) / DEMO_BENEFITS.length);

  const realizationPct = Math.round(totalRealized / totalPlanned * 100);
  const projectionPct  = Math.round(totalProjected / totalPlanned * 100);

  // Waterfall data for gap analysis
  const waterfallData = DEMO_BENEFITS.map(b => ({
    name: DEMO_PROJECTS.find(p => p.id === b.project)?.name.split(" ").slice(0, 3).join(" ") || b.project,
    planned: b.planned,
    projected: b.projected,
    realized: b.realized,
    gap: b.gap,
    confidence: b.confidence,
  }));

  // Time series — quarterly benefits realization forecast
  const quarterlyData = [
    { q: "Q1 2025", realized: 420000, forecast: 0,        planned: 2100000 },
    { q: "Q2 2025", realized: 1240000,forecast: 0,        planned: 3800000 },
    { q: "Q3 2025", realized: 2100000,forecast: 0,        planned: 5200000 },
    { q: "Q4 2025", realized: 3100000,forecast: 3500000,  planned: 7400000 },
    { q: "Q1 2026", realized: 0,       forecast: 5800000, planned: 10200000 },
    { q: "Q2 2026", realized: 0,       forecast: 9100000, planned: 13500000 },
    { q: "Q3 2026", realized: 0,       forecast: 13400000,planned: 17000000 },
    { q: "Q4 2026", realized: 0,       forecast: 16800000,planned: 19750000 },
  ];

  return (
    <PageLayout
      title="Benefits Realization Tracker"
      subtitle="Planned vs projected vs actual benefits, realization probability by initiative — P-03"
    >
      {/* KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="Total Planned"     value={fmt(totalPlanned)}   color={theme.textSecondary} sub="Portfolio target" />
        <KPICard label="Total Projected"   value={fmt(totalProjected)} color={totalProjected >= totalPlanned ? theme.success : theme.warning}
          sub={`${projectionPct}% of plan`} trend={projectionPct - 100} />
        <KPICard label="Total Realized"    value={fmt(totalRealized)}  color={theme.accent} sub={`${realizationPct}% realized`} />
        <KPICard label="Benefits Gap"      value={fmt(Math.abs(totalGap))} color={totalGap > 0 ? theme.danger : theme.success}
          sub={totalGap > 0 ? "Shortfall vs plan" : "Exceeds plan"} />
        <KPICard label="Avg Confidence"    value={`${avgConfidence}%`} color={avgConfidence >= 80 ? theme.success : avgConfidence >= 60 ? theme.warning : theme.danger}
          sub="ML confidence score" />
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        {/* Benefits Comparison Bar */}
        <div className="pmo-card">
          <div className="section-title">Planned vs Projected vs Realized by Initiative</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={waterfallData} margin={{ top: 5, right: 10, bottom: 30, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="name" tick={{ fill: theme.textMuted, fontSize: 9, angle: -35, textAnchor: "end" }} />
              <YAxis tickFormatter={v => `$${(v/1000000).toFixed(1)}M`} tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Bar dataKey="planned"   fill={`${theme.textMuted}66`} name="Planned"   />
              <Bar dataKey="projected" fill={theme.warning}           name="Projected" />
              <Bar dataKey="realized"  fill={theme.success}           name="Realized"  />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Quarterly Cumulative */}
        <div className="pmo-card">
          <div className="section-title">Cumulative Benefits Trajectory</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={quarterlyData} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <defs>
                <linearGradient id="planGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.textMuted} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.textMuted} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="fcGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.warning} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.warning} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="realGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.success} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={theme.success} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="q" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis tickFormatter={v => `$${(v/1000000).toFixed(0)}M`} tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Area type="monotone" dataKey="planned"  stroke={theme.textMuted} fill="url(#planGrad)" strokeWidth={1} name="Planned" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="forecast" stroke={theme.warning}   fill="url(#fcGrad)"   strokeWidth={2} name="Forecast" />
              <Area type="monotone" dataKey="realized" stroke={theme.success}   fill="url(#realGrad)" strokeWidth={2} name="Realized" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Benefits Detail Table */}
      <div className="pmo-card">
        <div className="section-title">Benefits Register — Initiative-Level Detail</div>
        <table>
          <thead>
            <tr>{["Project","Initiative","Planned","Projected","Realized","Gap","Confidence","Realization Period","ML Assessment"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {DEMO_BENEFITS.map((b, i) => {
              const onTrack = b.projected >= b.planned * 0.9;
              return (
                <tr key={i}>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{b.project}</td>
                  <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{b.name}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(b.planned)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: b.projected >= b.planned ? theme.success : theme.warning }}>{fmt(b.projected)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: b.realized > 0 ? theme.success : theme.textMuted }}>
                    {b.realized > 0 ? fmt(b.realized) : "Pending"}
                  </td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: b.gap > 0 ? theme.danger : theme.success }}>
                    {b.gap > 0 ? `-${fmt(b.gap)}` : `+${fmt(Math.abs(b.gap))}`}
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{ flex: 1, height: "4px", background: theme.cardBorder, borderRadius: "2px", minWidth: "50px" }}>
                        <div style={{ width: `${b.confidence}%`, height: "100%", background: b.confidence >= 80 ? theme.success : b.confidence >= 60 ? theme.warning : theme.danger, borderRadius: "2px" }} />
                      </div>
                      <span style={{ fontSize: "11px", color: b.confidence >= 80 ? theme.success : b.confidence >= 60 ? theme.warning : theme.danger }}>{b.confidence}%</span>
                    </div>
                  </td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: "11px" }}>{b.period}</td>
                  <td><RAGBadge status={onTrack ? "green" : b.confidence < 50 ? "red" : "amber"} label={onTrack ? "ON PLAN" : b.confidence < 50 ? "AT RISK" : "MONITOR"} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
